import React, { useState, useContext } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import useAddress from "../hooks/useAddress";
import { AuthContext } from "../hooks/useLogin";
import Navbar from "../Components/Navbar";
import NewAddress from "./NewAddress";
import EditAddress from "./EditAddress";
// Adjust the path to your logo image
import logo from "../Assets/logo.png"; // Ensure this path is correct

const YourAddresses = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  // eslint-disable-next-line no-unused-vars
  const { addresses, loading, error, setAddresses, deleteAddress } = useAddress(user?.id);
  const { selectedProducts = [], totalPrice = 0 } = location.state || {};

  const handleSelectAddress = (address) => {
    navigate("/ordersummary", {
      state: {
        selectedAddress: address,
        selectedProducts,
        totalPrice,
      },
    });
  };

  const handleEdit = (index) => {
    setSelectedIndex(index);
    setIsEditing(true);
  };

  const handleDeleteAddress = async (index) => {
    setDeleteIndex(index);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteIndex !== null) {
      const addressId = addresses[deleteIndex].id;
      await deleteAddress(addressId);
      setShowDeleteConfirm(false);
      setDeleteIndex(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteIndex(null);
  };

  const handleCloseNewAddress = () => {
    setIsAdding(false);
  };

  const handleCloseModal = () => {
    setIsEditing(false);
    setSelectedIndex(null);
  };

  return (
    <>
      <GlobalStyle />
      <Navbar />
      <Container>
        <AddressContainer>
          <Header>Your Address</Header>
          <AddAddress>
            <button onClick={() => setIsAdding(true)}>+ Add Address</button>
          </AddAddress>
        </AddressContainer>
   
        <AddressList>
          {loading ? (
            <LoadingPlaceholder />
          ) : error ? (
            <p>{error}</p>
          ) : addresses && addresses.length > 0 ? (
            addresses.map((address, index) => (
              <AddressCard 
                key={index} 
                onClick={() => handleSelectAddress(address)}
              >
                <AddressInfo>
                  <Name>{address.contact.name}</Name>
                  <Phone>{address.contact.phone}</Phone>
                  <AddressText>
                    {address.location.home}, {address.location.barangay}, {address.location.municipality}
                  </AddressText>
                </AddressInfo>
                <Actions onClick={(e) => e.stopPropagation()}>
                  <EditButton onClick={() => handleEdit(index)}>Edit</EditButton>
                  <DeleteButton onClick={() => handleDeleteAddress(index)}>Delete</DeleteButton>
                </Actions>
              </AddressCard>
            ))
          ) : (
            <NoAddresses>No addresses saved.</NoAddresses>
          )}
        </AddressList>
        {isEditing && selectedIndex !== null && (
          <ModalBackdrop onClick={handleCloseModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <EditAddress
                index={selectedIndex}
                address={addresses[selectedIndex]}
                onClose={handleCloseModal}
              />
            </ModalContent>
          </ModalBackdrop>
        )}

        {isAdding && (
          <ModalBackdrop onClick={handleCloseNewAddress}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <NewAddress onClose={handleCloseNewAddress} />
            </ModalContent>
          </ModalBackdrop>
        )}

        {showDeleteConfirm && (
          <ModalBackdrop onClick={handleCancelDelete}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ConfirmText>Are you sure you want to delete this address?</ConfirmText>
              <ConfirmButtons>
                <ConfirmButton onClick={handleConfirmDelete}>OK</ConfirmButton>
                <CancelButton onClick={handleCancelDelete}>Cancel</CancelButton>
              </ConfirmButtons>
            </ModalContent>
          </ModalBackdrop>
        )}
      </Container>
    </>
  );
};

// LoadingPlaceholder Component
const LoadingPlaceholder = () => (
  <LoadingContainer>
    <LoadingWrapper>
      <LogoSpinnerContainer>
        <LoadingLogo src={logo} alt="Loading Logo" />
        <InnerSpinner />
      </LogoSpinnerContainer>
      <LoadingText>LOADING</LoadingText>
    </LoadingWrapper>
  </LoadingContainer>
);

export default YourAddresses;

// Animation Keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(20px);
  }
`;

const scaleUp = keyframes`
  from {
    transform: scale(1);
  }
  to {
    transform: scale(1.05);
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const dots = keyframes`
  0% { content: "."; }
  33% { content: ".."; }
  66% { content: "..."; }
  100% { content: "."; }
`;

// Styled Components
const GlobalStyle = createGlobalStyle`
  body {
    font-family: Arial, sans-serif;
    background-color: #eaf4e1;
    margin: 0;
    padding: 0;
  }
`;

const Container = styled.div`
  max-width: 700px;
  margin: auto;
  padding: 20px;
`;

const AddressContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 10px 0;
`;

const Header = styled.h1`
  text-align: center;
  color: green;
`;

const AddAddress = styled.div`
  text-align: center;
  margin: 20px 0;

  button {
    background: green;
    color: white;
    border: none;
    padding: 10px 20px;
    font-size: 16px;
    cursor: pointer;
    border-radius: 5px;

    &:hover {
      background: darkgreen;
    }
  }
`;

const AddressList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AddressCard = styled.div`
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: lightgray;
    animation: ${scaleUp} 0.3s ease forwards;
  }
`;

const AddressInfo = styled.div`
  flex: 1;
`;

const Name = styled.h3`
  margin: 0;
  color: #333;
`;

const Phone = styled.p`
  margin: 5px 0;
  color: #666;
`;

const AddressText = styled.p`
  margin: 5px 0;
  color: #666;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: green;
  font-size: 16px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    animation: ${scaleUp} 0.3s ease forwards;
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: green;
  font-size: 16px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    animation: ${scaleUp} 0.3s ease forwards;
  }
`;

const NoAddresses = styled.p`
  text-align: center;
  color: #777;
`;

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease forwards;

  &.fade-out {
    animation: ${fadeOut} 0.3s ease forwards;
  }
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 10px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 0.3s ease forwards;

  &.fade-out {
    animation: ${fadeOut} 0.3s ease forwards;
  }
`;

const ConfirmText = styled.p`
  text-align: center;
  margin-bottom: 20px;
  color: #333;
`;

const ConfirmButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

const ConfirmButton = styled.button`
  background: green;
  color: white;
  border: none;
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
  border-radius: 5px;

  &:hover {
    background: darkgreen;
    animation: ${scaleUp} 0.3s ease forwards;
  }
`;

const CancelButton = styled.button`
  background: none;
  border: 1px solid green;
  color: green;
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
  border-radius: 5px;

  &:hover {
    background: #f0f0f0;
    animation: ${scaleUp} 0.3s ease forwards;
  }
`;

// Styled Components for Loading State
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 500px; /* Adjusted to match the inline style */
  background-color: #eaf4e1;
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center; /* Fixed typo: align-items should be camelCase in styled-components */
  gap: 10px;
`;

const LogoSpinnerContainer = styled.div`
  position: relative;
  width: 50px;
  height: 50px;
`;

const LoadingLogo = styled.img`
  width: 50px;
  height: 50px;
  z-index: 1;
  border-radius: 50%;
`;

const InnerSpinner = styled.div`
  position: absolute;
  top: -5px;
  left: -5px;
  width: 50px;
  height: 50px;
  border: 5px solid rgba(255, 255, 255, 0.5);
  border-top: 3px solid #004d00;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  pointer-events: none;
`;

const LoadingText = styled.span`
  color: #004d00;
  font-size: 1.2rem;
  font-weight: bold;

  &::after {
    content: ".";
    animation: ${dots} 1.5s steps(3, end) infinite;
    margin-left: 5px;
  }
`;